import classNames from 'classnames';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

import {
  BANNER_HEIGHT,
  CONTRACT_DETAILS_PAGE,
  DESKTOP_APP_BAR_HEIGHT,
} from 'src/constants';
import { RouteContext } from 'src/context';
import { ContractBuilderContext } from 'src/context/contractBuilderContext';
import { useBannerOptions } from 'src/hooks/useBannerOptions';
import { SignatureSidebarInputs } from 'src/legacy/components/Signature/SignatureSidebar';
import { TABS_CONTAINER_HEIGHT } from 'src/legacy/components/UI/BaseTab';
import { NonHoverBorder, white } from 'src/theme/colors';

import { AdvancedInputs } from './AdvancedInputs';
import { ContractInputsContainer } from './ContractInputsContainer';

interface StyleProps {
  topSpacing: number;
  height: number;
}

export const CONTRACT_SIDEBAR_WIDTH = 300;

const useStyles = makeStyles<StyleProps>()((_, props) => ({
  root: {
    position: 'fixed',
    top: props.topSpacing,
    right: 0,
    width: CONTRACT_SIDEBAR_WIDTH,
    height: props.height,
    borderLeft: `1px solid ${NonHoverBorder}`,
    overflow: 'auto',
    backgroundColor: white,
  },
  sidebarTopBorder: {
    borderTop: `1px solid ${NonHoverBorder}`,
  },
}));

/**
 *  Renders the right sidebar for creating contract templates.
 *  This sidebar includes different types of draggable inputs.
 *
 * @returns {JSX.Element} The JSX element rendering the contract template builder sidebar.
 */
export const ContractBuilderSidebar = () => {
  const { t } = useTranslation();
  const { pathname } = useContext(RouteContext);
  const bannerOptions = useBannerOptions();
  const isContractEditPage = pathname === CONTRACT_DETAILS_PAGE.path;

  // Calculate the top spacing and height of the sidebar based on the page type (Edit or Create Contract):
  // - On the Edit page: Include the page header, tabs toggle container, and trail banner (if any).
  // - On the Create Contract page: Include the page header and trail banner (if any).
  const sidebarStyles = useMemo(() => {
    const topSpacing = isContractEditPage
      ? DESKTOP_APP_BAR_HEIGHT +
        TABS_CONTAINER_HEIGHT +
        (bannerOptions ? BANNER_HEIGHT : 0)
      : DESKTOP_APP_BAR_HEIGHT + (bannerOptions ? BANNER_HEIGHT : 0);

    const height = window.innerHeight - topSpacing;

    return { topSpacing, height };
  }, [isContractEditPage, bannerOptions]);

  const { classes } = useStyles(sidebarStyles);
  const { placeholderElement } = useContext(ContractBuilderContext);
  // List of draggable input sections for the contract sidebar
  const contractSidebarSections = [
    {
      title: t(($) => $.page_contracts.inputs.client.title),
      tooltip: t(($) => $.page_contracts.inputs.client.tooltip),
      Component: (
        <SignatureSidebarInputs
          isClientSignatureButton
          signaturePlaceholderComponentElement={placeholderElement}
        />
      ),
      defaultExpanded: true,
    },
    {
      title: t(($) => $.page_contracts.inputs.current_user.title),
      tooltip: t(($) => $.page_contracts.inputs.current_user.tooltip),
      Component: (
        <SignatureSidebarInputs
          signaturePlaceholderComponentElement={placeholderElement}
        />
      ),
      defaultExpanded: false,
    },
    {
      title: t(($) => $.page_contracts.inputs.advanced.title),
      Component: <AdvancedInputs />,
      defaultExpanded: false,
    },
  ];

  return (
    <div
      className={classNames(classes.root, {
        [classes.sidebarTopBorder]: isContractEditPage,
      })}
    >
      {contractSidebarSections.map(
        ({ title, tooltip, defaultExpanded, Component }) => (
          <ContractInputsContainer
            key={title}
            summary={title}
            tooltip={tooltip}
            defaultExpanded={defaultExpanded}
          >
            {Component}
          </ContractInputsContainer>
        ),
      )}
    </div>
  );
};
